import { AfterViewInit, Component, ElementRef, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { CHART, fmtCompact, fmtFull, labelIndices, niceTicks, shortDay } from './chart-kit';

export interface LineSeries {
  label: string;
  color: string;
  values: number[];
}

const HEIGHT = 200;
const AXIS_BAND = 26;
const LEFT = 52;
const TOP = 18;

/**
 * Lines over days on one axis: 2px lines, an optional 10% area wash for a
 * single series, a crosshair that snaps to the nearest day and a tooltip
 * listing every series there. Line ends carry the latest value when they do
 * not collide; a legend is shown for two or more series.
 */
@Component({
  selector: 'app-line-chart',
  template: `
    @if (series().length > 1) {
    <div class="legend">
      @for (s of series(); track s.label) {
      <span class="key"><span class="line-key" [style.background]="s.color"></span>{{ s.label }}</span>
      }
    </div>
    }
    <div class="plot" (pointerleave)="hover.set(null)">
      <svg [attr.width]="width()" [attr.height]="height" role="img" [attr.aria-label]="ariaLabel()"
        (pointermove)="onMove($event)">
        @for (t of ticks(); track t) {
        <line [attr.x1]="left" [attr.x2]="plotRight()" [attr.y1]="y(t)" [attr.y2]="y(t)"
          [attr.stroke]="t === 0 ? c.axis : c.grid" stroke-width="1" />
        <text [attr.x]="left - 8" [attr.y]="y(t) + 4" text-anchor="end" class="tick">{{ compact(t) }}</text>
        }
        @for (s of paths(); track s.label) {
        @if (s.area) { <path [attr.d]="s.area" [attr.fill]="s.color" fill-opacity="0.1" /> }
        <path [attr.d]="s.line" [attr.stroke]="s.color" stroke-width="2" fill="none" stroke-linejoin="round" stroke-linecap="round" />
        }
        @for (i of xLabels(); track i) {
        <text [attr.x]="x(i)" [attr.y]="baseline + 18" [attr.text-anchor]="i === dates().length - 1 && dates().length > 1 ? 'end' : 'middle'" class="tick">{{ short(dates()[i]) }}</text>
        }
        @if (hover() !== null) {
        <line [attr.x1]="x(hover()!)" [attr.x2]="x(hover()!)" [attr.y1]="top" [attr.y2]="baseline" [attr.stroke]="c.muted" stroke-width="1" />
        @for (s of series(); track s.label) {
        <circle [attr.cx]="x(hover()!)" [attr.cy]="y(s.values[hover()!])" r="4" [attr.fill]="s.color" [attr.stroke]="c.surface" stroke-width="2" />
        }
        } @else {
        @for (e of endLabels(); track e.label) {
        <circle [attr.cx]="e.x" [attr.cy]="e.y" r="4" [attr.fill]="e.color" [attr.stroke]="c.surface" stroke-width="2" />
        <text [attr.x]="e.x - 8" [attr.y]="e.labelY" text-anchor="end" class="end-label">{{ compact(e.value) }}</text>
        }
        }
        <rect [attr.x]="left" [attr.y]="top" [attr.width]="plotRight() - left" [attr.height]="baseline - top + axisBand"
          fill="transparent" tabindex="0" class="hit" [attr.aria-label]="ariaLabel()"
          (focus)="hover.set(dates().length - 1)" (blur)="hover.set(null)" (keydown)="onKey($event)" />
      </svg>
      @if (hover() !== null) {
      <div class="tip" [style.left.px]="tipLeft()" [style.top.px]="top">
        <div class="tip-date">{{ dates()[hover()!] }}</div>
        @for (s of series(); track s.label) {
        <div class="tip-row"><span class="line-key" [style.background]="s.color"></span><b>{{ full(s.values[hover()!]) }}</b><span>{{ s.label }}</span></div>
        }
      </div>
      }
    </div>
  `,
  styleUrl: './chart.scss',
})
export class LineChart implements AfterViewInit, OnDestroy {
  readonly dates = input.required<string[]>();
  readonly series = input.required<LineSeries[]>();
  /** A 10% wash under the line; only for a single series. */
  readonly area = input(false);
  readonly ariaLabel = input('Line chart');

  readonly c = CHART;
  readonly height = HEIGHT + AXIS_BAND;
  readonly top = TOP;
  readonly left = LEFT;
  readonly baseline = HEIGHT;
  readonly axisBand = AXIS_BAND;
  readonly compact = fmtCompact;
  readonly full = fmtFull;
  readonly short = shortDay;

  private host = inject(ElementRef<HTMLElement>);
  private observer?: ResizeObserver;
  readonly width = signal(600);
  readonly hover = signal<number | null>(null);

  readonly plotRight = computed(() => this.width() - 12);
  readonly ticks = computed(() => niceTicks(Math.max(0, ...this.series().flatMap(s => s.values))));
  private readonly yMax = computed(() => this.ticks()[this.ticks().length - 1] || 1);

  x(i: number): number {
    const n = this.dates().length;
    return n <= 1 ? (LEFT + this.plotRight()) / 2 : LEFT + (i / (n - 1)) * (this.plotRight() - LEFT);
  }

  y(v: number): number {
    return HEIGHT - (Math.max(0, v) / this.yMax()) * (HEIGHT - TOP);
  }

  readonly paths = computed(() => this.series().map(s => {
    const pts = s.values.map((v, i) => `${this.x(i).toFixed(1)},${this.y(v).toFixed(1)}`);
    const line = pts.length ? 'M' + pts.join(' L') : '';
    const area = this.area() && this.series().length === 1 && pts.length > 1
      ? `${line} L${this.x(s.values.length - 1).toFixed(1)},${HEIGHT} L${this.x(0).toFixed(1)},${HEIGHT} Z`
      : '';
    return { label: s.label, color: s.color, line, area };
  }));

  /** The latest value at each line end; dropped when two ends would overlap. */
  readonly endLabels = computed(() => {
    const last = this.dates().length - 1;
    if (last < 0) return [];
    const ends = this.series().map(s => ({
      label: s.label, color: s.color, value: s.values[last] ?? 0,
      x: this.x(last), y: this.y(s.values[last] ?? 0), labelY: 0,
    }));
    for (const e of ends) e.labelY = e.y < TOP + 14 ? e.y + 16 : e.y - 8;
    const collide = ends.some((a, i) => ends.some((b, j) => i < j && Math.abs(a.labelY - b.labelY) < 14));
    return collide ? [] : ends;
  });

  readonly xLabels = computed(() => labelIndices(this.dates().length, Math.max(2, Math.floor((this.width() - LEFT) / 64))));

  readonly tipLeft = computed(() => {
    const i = this.hover();
    if (i === null) return 0;
    const px = this.x(i);
    const tipW = 190;
    return px + 12 + tipW > this.width() ? Math.max(0, px - tipW - 12) : px + 12;
  });

  onMove(e: PointerEvent): void {
    const n = this.dates().length;
    if (n === 0) return;
    const svg = (e.currentTarget as SVGElement).getBoundingClientRect();
    const px = e.clientX - svg.left;
    const t = (px - LEFT) / Math.max(1, this.plotRight() - LEFT);
    this.hover.set(Math.min(n - 1, Math.max(0, Math.round(t * (n - 1)))));
  }

  onKey(e: KeyboardEvent): void {
    const i = this.hover() ?? this.dates().length - 1;
    if (e.key === 'ArrowLeft') { this.hover.set(Math.max(0, i - 1)); e.preventDefault(); }
    if (e.key === 'ArrowRight') { this.hover.set(Math.min(this.dates().length - 1, i + 1)); e.preventDefault(); }
  }

  ngAfterViewInit(): void {
    this.observer = new ResizeObserver(entries => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) this.width.set(w);
    });
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
